import { ActivityMaps, ActivityMapsHost, ClickFacet } from '../../page-insights/activitymaps';
import { OverlayMode } from '../../overlays/overlay';
import { ClickStats, createElemAndStats } from '../../page-insights/document_lookup';
import { WebPageRenderer } from '../renderer';
import { maxHighlightedElements } from '../../page-insights/activitymapsconst'; // Clamp the number of elements to highlight in a given activity map.
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { getAllDescendantWindowsFromDoc } from '@fullstory/util/iframe';
import {
  ClickAreaOverlayDisplayOptions,
  ClickMapDisplayOptions,
  ConversionOverlayDisplayOptions,
} from '@fullstory/page-insights-ui';
import { PlaybackContext, GroupByPropertyDisplay } from '@fullstory/renderer-shared';
import { elemIdent } from '../../page-insights/ident';

export class WebActivityMaps implements ActivityMaps, OverlayMode {
  _highlighted: { [key: string]: ClickStats } = {};
  _unhookScroll: () => void;
  _exportMode: boolean;

  constructor(
    private _renderer: WebPageRenderer,
    private _host: ActivityMapsHost,
    private _ctx: PlaybackContext,
  ) {}

  start() {}

  stop() {
    this.clear();
    this._unhookScroll?.();
    this._unhookScroll = undefined;
  }

  clear() {
    this._highlighted = {};
    this._renderer.overlays().clearAreaOverlays();
    this._renderer.overlays().clearHighlights();
  }

  enableMapInsights(enable: boolean) {
    this._renderer.view().switchClass('map-insights', enable);
  }

  setMapMode(enable: boolean) {
    if (enable) {
      this._renderer.view().addClass('is-map-mode');
    } else {
      this._renderer.view().removeClass('is-map-mode');
    }
  }

  setExportMode(enable: boolean) {
    if (enable) {
      this._renderer.view().addClass('export-mode');
    } else {
      this._renderer.view().removeClass('export-mode');
    }
    this._exportMode = enable;
  }

  highlightConversionZones(
    clickFacet: ClickFacet,
    displayOptions: ConversionOverlayDisplayOptions,
  ) {
    const { topClickedElems, filteredElementDetails, frame } = this.performActivityMapSetup({
      allowHighlightRelatedElements:
        this._ctx.flags.features.AppClickmapPossibleElementsHighlighting,
      showEngagementMaps: false,
      exportMode: displayOptions.exportMode,
      clicks: clickFacet,
      focusSelectors: displayOptions.focusSelectors || [],
      groupByProperty: displayOptions.groupByProperty,
    });

    if (!frame) {
      return;
    }

    this._renderer.overlays().placeConversionHighlights({
      host: this._host,
      elems: topClickedElems,
      displayOptions,
      playbackContainer: this._renderer.view().scaled,
    });

    // Convert our inside-the-frame ElemAndStats[] to just an ElemStats[]
    // suitable for sending over postMessage.
    const statistics = topClickedElems.map(x => x.stats());

    this._host.renderStats(
      clickFacet,
      statistics,
      NativePlatform.NATIVE_PLATFORM_WEB,
      filteredElementDetails,
    ); // Send it back out.

    const scroll = () => {
      this._renderer.overlays().layoutConversionMaps(displayOptions);
    };
    this._updateScroll(frame, scroll);
  }

  highlightClickArea(clickFacet: ClickFacet, displayOptions: ClickAreaOverlayDisplayOptions) {
    const { topClickedElems, filteredElementDetails, frame } = this.performActivityMapSetup({
      allowHighlightRelatedElements:
        this._ctx.flags.features.AppClickmapPossibleElementsHighlighting,
      showEngagementMaps: false,
      exportMode: false,
      clicks: clickFacet,
      focusSelectors: displayOptions.focusSelectors || [],
      groupByProperty: displayOptions.groupByProperty,
    });

    if (!frame) {
      return;
    }

    this._renderer.overlays().placeAreaHighlights({
      host: this._host,
      elems: topClickedElems,
      displayOptions,
      playbackContainer: this._renderer.view().scaled,
      clickFacet,
    });

    // Convert our inside-the-frame ElemAndStats[] to just an ElemStats[]
    // suitable for sending over postMessage.
    const statistics = topClickedElems.map(x => x.stats());

    this._host.renderStats(
      clickFacet,
      statistics,
      NativePlatform.NATIVE_PLATFORM_WEB,
      filteredElementDetails,
    ); // Send it back out.

    const scroll = () => {
      this._renderer.overlays().layoutAreaClickMaps(displayOptions);
    };
    this._updateScroll(frame, scroll);
  }

  highlight(clicks: ClickFacet, displayOptions: ClickMapDisplayOptions) {
    const { topClickedElems, filteredElementDetails, frame } = this.performActivityMapSetup({
      allowHighlightRelatedElements: displayOptions.allowHighlightRelatedElements,
      showEngagementMaps: displayOptions.showEngagementMaps,
      exportMode: displayOptions.exportMode,
      clicks,
      focusSelectors: displayOptions.focusSelectors || [],
      groupByProperty: displayOptions.groupByProperty,
    });

    if (!frame) {
      return;
    }

    this._renderer
      .overlays()
      .highlight(this._host, topClickedElems, displayOptions, this._exportMode);

    // Convert our inside-the-frame ElemAndStats[] to just an ElemStats[]
    // suitable for sending over postMessage.
    const statistics = topClickedElems.map(x => x.stats());

    this._host.renderStats(
      clicks,
      statistics,
      NativePlatform.NATIVE_PLATFORM_WEB,
      filteredElementDetails,
    ); // Send it back out.

    const scroll = () => {
      this._renderer.overlays().layout(displayOptions.allowHighlightRelatedElements, false);
    };
    this._updateScroll(frame, scroll);
  }

  select(selector: string, propertyValue: string | undefined, selected: boolean = true) {
    if (!selected) {
      // Deprecated option. Do nothing. This option will be removed.
      return;
    }
    const e = this._highlighted[elemIdent(selector, propertyValue)];
    if (e) {
      // Highlight the new element in the overlay.
      this._renderer.overlays().select(e);
    }
  }

  private _updateScroll(frame: HTMLIFrameElement, scroll: (e: Event) => void) {
    // First, unhook the existing scroll if necessary.
    this._unhookScroll?.();

    // Then wire up the new scroll
    window.addEventListener('scroll', scroll, true);
    // Add scroll listeners to child iframes as well in case that get scrolled
    const childWindows = getAllDescendantWindowsFromDoc(frame.contentWindow);
    for (const childWindow of childWindows) {
      childWindow.addEventListener('scroll', scroll, true);
    }

    // And finally wire up the new unhook scroll for the given windows
    this._unhookScroll = () => {
      window.removeEventListener('scroll', scroll, true);
      for (const childWindow of childWindows) {
        childWindow.removeEventListener('scroll', scroll, true);
      }
    };
  }

  /**
   * Performs common setup when using a click map type of activity map. Note that the returned frame needs to be checked.
   * If it's null, then return early as it doesn't exist.
   */
  private performActivityMapSetup({
    allowHighlightRelatedElements,
    showEngagementMaps,
    exportMode,
    clicks,
    focusSelectors,
    groupByProperty,
  }: {
    allowHighlightRelatedElements: boolean;
    showEngagementMaps: boolean;
    exportMode: boolean;
    clicks: ClickFacet;
    focusSelectors: string[];
    groupByProperty: GroupByPropertyDisplay | undefined;
  }) {
    this.clear();
    this.setMapMode(showEngagementMaps);
    this.setExportMode(exportMode);

    const frame = this._renderer.activeRootDocFrame();
    if (!frame) {
      return {};
    }

    const doc = frame.contentDocument;
    const window = frame.contentWindow;

    const { visibleClickElems, filteredElementDetails } = createElemAndStats({
      wnd: window,
      doc,
      renderer: this._renderer,
      facet: clicks,
      pageContext: this._ctx.page,
      allowHighlightRelatedElementsEnabled: allowHighlightRelatedElements,
      focusSelectors,
      groupByProperty,
    });

    visibleClickElems.forEach(e => {
      this._highlighted[e.identKey()] = e;
    });

    const limit = Math.min(maxHighlightedElements, visibleClickElems.length);
    const topClickedElems = visibleClickElems.slice(0, limit);

    return { topClickedElems, filteredElementDetails, frame };
  }
}
